import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';

export const initialFilterState = {
	gender: [],
	service_type: [],
	status: [],
	ranges: {
		current_state: {
			age: { min: 0, max: 0 },
			price: { min: 0, max: 0 },
			height: { min: 0, max: 0 },
			weight: { min: 0, max: 0 },
			penis: { min: 0, max: 0 },
		},
		default_state: {
			age: { min: 0, max: 0 },
			price: { min: 0, max: 0 },
			height: { min: 0, max: 0 },
			weight: { min: 0, max: 0 },
			penis: { min: 0, max: 0 },
		},
	},

	// secondary filters without url handling
	orientation: [],

	// recall appearance is used for appearance values which are like statuses applied with =1
	appearance: [],

	lips: [],
	penis: [],
	hair: [],
	breast: [],
	hair_len: [],
	intim_hs: [],
	eyes: [],
	smoking: [],
	tattoo: [],
	piercing: [],
	services: [],
	for: [],
	languages: [],
	nationality: [],
	ethnos: [],
	last_visit: [],
	min_time: [],

	// done separator

	radius: {
		value: [],
		latitude: 0,
		longitude: 0,
	},
};

export const initialOtherState = {
	user_address: null,
	search_nick: null,
	filter_query: [],
	profiles_on_map_count: 0,
	profiles_count: 0,
	disabled_filters: [],
	fast_filter: false,
	agency_id_filter: null,
	sort_param: 'date',
	filter_edit_obj: null,
};

const createResetState = (currentState) => ({
	...initialFilterState,
	search_nick: null,
	ranges: {
		current_state: currentState.ranges.default_state,
		default_state: currentState.ranges.default_state,
	},
	user_address: currentState.user_address,
	filter_query: [],
	profiles_on_map_count: 0,
	profiles_count: 0,
	disabled_filters: [],
	fast_filter: false,
	agency_id_filter: currentState.agency_id_filter,
	sort_param: currentState.sort_param,
	filter_edit_obj: currentState.filter_edit_obj,
});

export const filterState = createSlice({
	name: 'filter',
	initialState: {
		...initialFilterState,
		...initialOtherState,
	},
	reducers: {
		// can be used with any objects of type {gender: ['male']} to update only present attributes, or empy payload - leads to reset all
		updateFilter: (state, action) => {
			if (!action.payload) {
				return createResetState(state);
			}

			// console.log(`update`, action.payload);

			Object.assign(state, {
				...state,
				...action.payload,
				ranges: {
					...state.ranges,
					...action.payload?.ranges,
				},
			});

			// const stateString = JSON.stringify(state, null, 2);
			// const stateObject = JSON.parse(stateString);

			// console.group('!!! FILTER CHANGE');
			// console.trace('➡️  STATE AFTER UPDATE:', stateObject);
			// console.log('➡️  PAYLOAD:', action.payload);
			// console.groupEnd();

			// console.trace('UPDATE: filterState update:', stateObject);
			// console.log(`update ended`, action.payload);
		},

		// pass object of type {key: 'somekey', payload: ['toggleItem1', 'toggleItem2'] or just 'toggleItem'}
		toggleSomeFilter: (state, action) => {
			const { key, payload } = action.payload;

			// console.log('=> TOGGLE SOME FILTER WITH', action.payload);

			const mapkey = key === 'place' ? 'service_type' : key;

			const itemsToToggle = Array.isArray(payload) ? payload : [payload];

			const validFilterKeys = Object.keys(initialFilterState);

			if (!validFilterKeys.includes(mapkey)) {
				console.info(
					`toggleSomeFilter: Invalid key "${mapkey}". Allowed:`,
					validFilterKeys,
				);
				return;
			}

			// console.log(`toggle`, mapkey, payload);

			state[mapkey] = state[mapkey]
				.filter((existingItem) => !itemsToToggle.includes(existingItem))
				.concat(
					itemsToToggle.filter(
						(newItem) => !state[mapkey].includes(newItem),
					),
				);

			// console.log('После обновления:', state[mapkey]);

			return state;
		},

		updateCurrentRanges: (state, action) => {
			const { key, values } = action.payload;

			const validRangesKeys = Object.keys(
				initialFilterState.ranges.current_state,
			);

			if (validRangesKeys.includes(key)) {
				const new_ranges = {
					...state.ranges.current_state,
					[key]: values,
				};
				state.ranges.current_state = new_ranges;
			} else {
				console.error(
					`=> wrong key for ranges: `,
					key,
					'; valid: ',
					validRangesKeys,
				);
			}
		},

		// use an object {default_state: ..., unchangedRanges: [...]}
		resetUnchangedRanges: (state, action) => {
			const { unchangedRanges, default_state } = action.payload;
			unchangedRanges.forEach((rangeKey) => {
				state.ranges.current_state[rangeKey] = default_state[rangeKey];
			});
		},

		setDefaultRangeState: (state, action) => {
			state.ranges.default_state = action.payload;
			// if (state.ranges.current_state)
			// 	state.ranges.current_state = action.payload;
			const updatedRanges = { ...state.ranges.current_state };

			Object.keys(state.ranges.current_state).forEach((key) => {
				const current = state.ranges.current_state[key];
				const defaults = state.ranges.default_state[key];

				if (current.min === 0 && current.max === 0) {
					updatedRanges[key] = { ...defaults };
					return;
				}

				if (current.min < defaults.min) {
					updatedRanges[key].min = defaults.min;
				}

				// Проверяем max: если current.max > default.max → обновляем
				if (current.max > defaults.max) {
					updatedRanges[key].max = defaults.max;
				}

				if (updatedRanges[key].min > updatedRanges[key].max) {
					updatedRanges[key].min = updatedRanges[key].max;
				}
			});

			state.ranges.current_state = updatedRanges;
		},

		setUserAddress: (state, action) => {
			state.user_address = action.payload;
		},

		setFilterEditObj: (state, action) => {
			state.filter_edit_obj = action.payload;
		},

		setSearchNick: (state, action) => {
			state.search_nick = action.payload;
		},

		setProfilesCount: (state, action) => {
			state.profiles_count = action.payload;
		},

		setProfilesOnMapCount: (state, action) => {
			state.profiles_on_map_count = action.payload;
		},

		setDisabledFilters: (state, action) => {
			try {
				state.disabled_filters = action.payload;
			} catch (e) {
				console.error(e);
			}
		},

		setFilterQuery: (state, action) => {
			state.filter_query = action.payload;
		},

		setFastFilter: (state, action) => {
			state.fast_filter = action.payload;
		},

		setFilterAgencyId: (state, action) => {
			state.agency_id_filter = action.payload;
		},

		setSortParam: (state, action) => {
			state.sort_param = action.payload;
		},
	},
	devTools: true,
});

export const {
	updateFilter,
	setProfilesOnMapCount,
	setFilterEditObj,
	setUserAddress,
	setSearchNick,
	setProfilesCount,
	setDisabledFilters,
	setFilterQuery,
	setDefaultRangeState,
	toggleSomeFilter,
	setFastFilter,
	setFilterAgencyId,
	updateCurrentRanges,
	setSortParam,
	resetUnchangedRanges,
} = filterState.actions;

export const getFilterState = (state) => state.filter;
export const getFilterEditObj = (state) => state.filter.filter_edit_obj;
export const getUserAddress = (state) => state.filter.user_address;
export const getSearchNick = (state) => state.filter.search_nick;
export const getAllProfilesCount = (state) => state.filter.profiles_count;
export const getProfilesOnMapCount = (state) =>
	state.filter.profiles_on_map_count;
export const getDisabledFilters = (state) => state.filter.disabled_filters;
export const getFilterQuery = (state) => state.filter.filter_query;
export const getFilterRanges = (state) => state.filter.ranges.current_state;
export const getFastFilters = (state) => state.filter.fast_filter;
export const getAgencyFilterID = (state) => state.filter.agency_id_filter;
export const getSortParam = (state) => state.filter.sort_param;

export const syncronomousToggleSomeFilter = (
	current_state,
	{ key, payload },
) => {
	const mapkey = key === 'place' ? 'service_type' : key;

	const itemsToToggle = Array.isArray(payload) ? payload : [payload];

	const validFilterKeys = Object.keys(initialFilterState);

	if (!validFilterKeys.includes(mapkey)) {
		console.info(
			`SYNC toggleSomeFilter: Invalid key "${mapkey}". Allowed:`,
			validFilterKeys,
		);
		return;
	}

	let new_obj = {
		...current_state,
		[mapkey]: current_state[mapkey]
			.filter((existingItem) => !itemsToToggle.includes(existingItem))
			.concat(
				itemsToToggle.filter(
					(newItem) => !current_state[mapkey].includes(newItem),
				),
			),
	};

	// console.log('После обновления:', new_obj);

	return new_obj;
};

export const syncronomousUpdateFilter = (
	current_state,
	payload,
	radio = false,
) => {
	if (!payload) {
		if (!current_state) {
			return initialFilterState;
		} else {
			return {
				...initialFilterState,
				ranges: {
					current_state: current_state.ranges.default_state,
					default_state: current_state.ranges.default_state,
				},
			};
		}
	}

	// тот же контракт: пустой payload — полный reset
	// формируем новый объект, не мутируя current_state

	const nextState = {
		...current_state,
		...payload,
		ranges: {
			...current_state.ranges,
			...payload?.ranges,
		},
	};

	return nextState;
};

export const syncronomousUpdateRangesFilter = (current_state, payload) => {
	const { key, values } = payload;

	const nextState = {
		...current_state,
		ranges: {
			...current_state.ranges,
			current_state: {
				...current_state.ranges.current_state,
				[key]: values,
			},
		},
	};

	return nextState;
};

export default filterState.reducer;
